import { css } from '@emotion/react';
import { type FC, type HTMLAttributes, type ReactNode } from 'react';

import { Text } from '../Typography';

export type StepListStep = {
  title: ReactNode;
  description: ReactNode;
};

export type StepListProps = HTMLAttributes<HTMLOListElement> & {
  steps: StepListStep[];
  /** Heading element for each step title. Defaults to `h3`. */
  titleAs?: 'h3' | 'h4';
};

const listStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--spacing-10);
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
`;

const itemStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  counter-increment: step;

  &::before {
    content: counter(step);
    display: grid;
    place-items: center;
    width: var(--spacing-11);
    height: var(--spacing-11);
    border-radius: 50%;
    background: var(--surface-forest);
    color: var(--color-base-white);
    font-weight: 800;
    font-size: 17px;
  }
`;

const titleStyles = css`
  && {
    margin: 0;
    font-size: 21px;
    line-height: 1.3;
    font-weight: 800;
  }
`;

/**
 * Numbered steps laid out in columns, for "how it works" content.
 */
const StepList: FC<StepListProps> = ({
  steps,
  titleAs = 'h3',
  className,
  ...rest
}) => (
  <ol {...rest} className={className} css={listStyles}>
    {steps.map((step, index) => (
      <li key={index} css={itemStyles}>
        <Text as={titleAs} color="var(--color-primary-800)" css={titleStyles}>
          {step.title}
        </Text>
        <Text
          as="p"
          color="var(--color-neutral-800)"
          css={{ margin: 0, lineHeight: 1.65 }}
        >
          {step.description}
        </Text>
      </li>
    ))}
  </ol>
);

StepList.displayName = 'StepList';

export default StepList;
